import { useState } from 'react';
import { Box, Typography, CircularProgress, TablePagination } from '@mui/material';
import { toast } from 'react-toastify';
import { useSavedQueries, SavedQuery } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { useSaveQuery, SaveQueryParams, QUERY_NAME_TAKEN, QUERY_USED_IN_CAMPAIGN, type QueryRefusal } from '../../../../hooks/QueryBuilder/useSaveQuery';
import { useQueryBuilderData } from '../../../../context/QueryBuilderDataContext';
import { useLanguage } from '../../../../context/LanguageContext';
import { QuerySource } from '../../../../types/QueryBuilder';
import EditQueryDialog from './EditQueryDialog';
import QueryResultDialog from './QueryResultDialog';
import SavedQueriesTable from './SavedQueriesTable';
import { FilterHeader } from './FilterHeader';

interface MyQueriesTabProps {
    refreshKey?: number;
}

export function MyQueriesTab({ refreshKey = 0 }: MyQueriesTabProps) {
    const { t } = useLanguage();
    const { groups: availableGroups, loadingGroups } = useQueryBuilderData();
    const { saveQuery } = useSaveQuery();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
    const [isFromCompany, setIsFromCompany] = useState<0 | 1 | undefined>(undefined);
    const [source, setSource] = useState<QuerySource | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
    const [editedQuery, setEditedQuery] = useState<SavedQuery | null>(null);
    const [viewedQuery, setViewedQuery] = useState<SavedQuery | null>(null);

    const { queries, totalItems, loading, refetch } = useSavedQueries({
        page: page + 1,
        itemsPerPage: rowsPerPage,
        groupQueryIds: selectedGroupIds.length > 0 ? selectedGroupIds.join(',') : undefined,
        isFromCompany,
        search: submittedSearchTerm,
        source,
    });

    const [lastRefreshKey, setLastRefreshKey] = useState(refreshKey);
    if (refreshKey !== lastRefreshKey) {
        setLastRefreshKey(refreshKey);
        refetch();
    }

    const resettingPage = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        setPage(0);
    };

    const handleReset = () => {
        setSelectedGroupIds([]);
        setIsFromCompany(undefined);
        setSource(undefined);
        setSearchTerm('');
        setSubmittedSearchTerm('');
        setPage(0);
    };

    const handleSearchSubmit = (value: string) => {
        const search = value.trim();
        setPage(0);
        if (search === submittedSearchTerm && page === 0) {
            refetch();
            return;
        }
        setSubmittedSearchTerm(search);
    };

    // Renvoie le refus de l'API (nom pris, requête utilisée dans une campagne) : la fenêtre d'édition reste alors ouverte
    const handleSave = async (params: SaveQueryParams): Promise<true | QueryRefusal> => {
        const toastId = params.queryId !== undefined ? toast.loading('Modification de la requête en cours...') : null;
        const result = await saveQuery({ ...params, uniqueName: true });
        if (result === QUERY_NAME_TAKEN || result === QUERY_USED_IN_CAMPAIGN) {
            const render = result === QUERY_NAME_TAKEN
                ? t('pages.clients.queryBuilder.queryNameTakenNamed', { name: params.description })
                : t('pages.clients.queryBuilder.usedInCampaignCopyRequired');
            if (toastId) toast.update(toastId, { render, type: 'warning', isLoading: false, autoClose: 5000 });
            else toast.warning(render);
            return result;
        }
        if (toastId) {
            toast.update(toastId, {
                render: !result
                    ? 'Erreur lors de la mise à jour'
                    : result.countInvalidated
                        ? 'Requête mise à jour. Ses critères ont changé : relancez le comptage.'
                        : 'Requête mise à jour avec succès',
                type: result ? 'success' : 'error',
                isLoading: false,
                autoClose: 5000,
            });
        } else if (result) {
            toast.success(`Requête « ${params.description} » sauvegardée`, { autoClose: 3000 });
        } else {
            toast.error('Erreur lors de la sauvegarde de la requête');
        }
        refetch();
        setEditedQuery(null);
        return true;
    };

    return (
        <Box>
            <FilterHeader
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchSubmit={handleSearchSubmit}
                selectedGroupIds={selectedGroupIds}
                onSelectedGroupIdsChange={resettingPage(setSelectedGroupIds)}
                availableGroups={availableGroups}
                loadingGroups={loadingGroups}
                isFromCompany={isFromCompany}
                onTypeChange={resettingPage(setIsFromCompany)}
                source={source}
                onSourceChange={resettingPage(setSource)}
                onReset={handleReset}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : queries.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body2" color="text.secondary">{t('pages.clients.queryBuilder.noQueryFound')}</Typography>
                </Box>
            ) : (
                <>
                    <SavedQueriesTable
                        queries={queries}
                        onView={setViewedQuery}
                        onEdit={setEditedQuery}
                        onTypeFilter={resettingPage(setIsFromCompany)}
                        onSourceFilter={resettingPage(setSource)}
                    />
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={event => resettingPage(setRowsPerPage)(parseInt(event.target.value, 10))}
                        labelRowsPerPage={t('common.rowsPerPage')}
                        sx={{ mt: 1 }}
                    />
                </>
            )}

            <EditQueryDialog
                open={editedQuery !== null}
                query={editedQuery}
                onClose={() => setEditedQuery(null)}
                onSave={handleSave}
            />

            <QueryResultDialog
                open={viewedQuery !== null}
                onClose={() => setViewedQuery(null)}
                queryId={viewedQuery?.QUERY_ID ?? null}
                description={viewedQuery?.DESCRIPTION || viewedQuery?.QUERY_NAME}
            />
        </Box>
    );
}

export default MyQueriesTab;
